import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BodyM, LabelS } from '@sorare/core/src/atoms/typography';
import {
  Dialog,
  Props as DialogProps,
} from '@sorare/core/src/components/dialog';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileAndAbove } from '@sorare/core/src/hooks/device/useIsMobileAndAbove';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import {
  getHumanReadableQuality,
  scarcityNames,
} from '@sorare/core/src/lib/cards';
import { shardsIconUrl } from '@sorare/core/src/lib/rewards';

import { EssenceOdds } from './EssenceOdds';
import {
  EssenceOddsDialogQuery,
  EssenceOddsDialogQueryVariables,
} from './__generated__/index.graphql';

const ESSENCE_ODDS_DIALOG_QUERY = gql`
  query EssenceOddsDialogQuery($sport: Sport!) {
    currentUser {
      slug
      cardShardsChests(sport: $sport) {
        id
        rarity
        thresholds {
          probabilities {
            basisPoints
            quality
          }
          threshold
          ...EssenceOdds_cardShardsChestThreshold
        }
      }
    }
  }
  ${EssenceOdds.fragments.cardShardsChestThreshold}
` as TypedDocumentNode<EssenceOddsDialogQuery, EssenceOddsDialogQueryVariables>;

const Root = styled(Vertical)`
  padding: var(--double-unit);
`;
const Threshold = styled(Vertical)`
  --color: var(--c-neutral-500);
  align-items: center;

  &.selected {
    --color: var(--c-brand-600);
  }
`;
const StyledButton = styled(ButtonBase)`
  width: calc(8 * var(--unit));
  height: calc(8 * var(--unit));
  border: 1px solid var(--color);
  border-radius: var(--unit);
  position: relative;

  img {
    width: 60%;
    height: 60%;
    object-fit: contain;
  }
`;
const ThresholdCost = styled(LabelS)`
  position: absolute;
  bottom: var(--half-unit);
  right: var(--half-unit);
`;

type Props = Pick<DialogProps, 'open' | 'onClose'> & { rarity: Rarity };

export const EssenceOddsDialog = ({ open, onClose, rarity }: Props) => {
  const isMobileAndAbove = useIsMobileAndAbove();
  const { sport } = useSportContext();
  const { data, loading } = useQuery(ESSENCE_ODDS_DIALOG_QUERY, {
    variables: { sport },
  });

  const { cardShardsChests } = data?.currentUser || {};
  const rarityChest = cardShardsChests?.find(chest => chest.rarity === rarity);

  const [selectedThreshold, setSelectedThreshold] = useState(
    rarityChest?.thresholds[0]
  );

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!rarityChest) {
    return null;
  }

  if (!selectedThreshold && rarityChest) {
    setSelectedThreshold(rarityChest.thresholds[0]);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      title={
        <BodyM as="p" bold className="text-center">
          <FormattedMessage
            id="Essence.EssenceOddsDialog.essenceThresholds"
            defaultMessage="{rarity} Essence thresholds"
            values={{ rarity: scarcityNames[rarity] }}
          />
        </BodyM>
      }
    >
      <Root gap={4}>
        <Scrollable
          itemToDisplay={isMobileAndAbove ? rarityChest.thresholds.length : 3}
        >
          {rarityChest.thresholds.map(threshold => {
            const highestProbability = (threshold?.probabilities ?? []).reduce<
              NonNullable<typeof threshold>['probabilities'][number] | null
            >(
              (acc, probability) =>
                probability.basisPoints > (acc?.basisPoints ?? 0)
                  ? probability
                  : acc,
              null
            );

            if (!highestProbability) {
              return null;
            }

            return (
              <Threshold
                key={threshold.threshold}
                className={classNames({
                  selected:
                    selectedThreshold?.threshold === threshold.threshold,
                })}
              >
                <StyledButton
                  onClick={() => setSelectedThreshold(threshold)}
                  aria-label={`threshold ${threshold.threshold}`}
                >
                  <ThresholdCost>{threshold.threshold}</ThresholdCost>
                  <img src={shardsIconUrl(rarity)} alt="" />
                </StyledButton>
                <LabelS
                  as="p"
                  color="var(--color)"
                  className="text-center"
                  bold
                >
                  {getHumanReadableQuality(highestProbability.quality)}+
                </LabelS>
              </Threshold>
            );
          })}
        </Scrollable>
        {selectedThreshold && (
          <EssenceOdds rarity={rarity} threshold={selectedThreshold} />
        )}
      </Root>
    </Dialog>
  );
};
